import React, { useState, useEffect, useRef } from "react";
import { ContentContainer } from "@layout";
import {
  Button,
  DownloadButton,
  LoadingComponent,
  NoDataFoundTemplate,
  Select,
} from "../../../components/core";
import { useLazyQuery } from "@apollo/client";
import { QUERY_HighRemovalCourier } from "../../../constants/graphql/operatrions";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Card } from "primereact/card";

export default function HighRemovalCourier() {
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadInce, setloadInce] = useState(false);
  const dt = useRef(null);
  const operatorId = JSON.parse(localStorage.getItem("user")).operator_id;
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [noResults, setNoResults] = useState(false);

  const [QUERYHighRemovalCourier, { data }] = useLazyQuery(
    QUERY_HighRemovalCourier,
    { fetchPolicy: "no-cache" },
  );

  const parseDecimalArray = (decimalString) => {
    try {
      // Convert "Decimal('6'), Decimal('0')" format to numbers
      const matches = decimalString.match(/Decimal\('([^']+)'\)/g);
      if (matches) {
        return matches.map((match) => {
          const num = match.match(/Decimal\('([^']+)'\)/)[1];
          return parseInt(num);
        });
      }
      return [];
    } catch (error) {
      console.error("Error parsing decimal array:", error);
      return [];
    }
  };

  // Helper function to parse phone number strings
  const parsePhoneArray = (phoneString) => {
    try {
      return JSON.parse(phoneString.replace(/'/g, '"'));
    } catch (error) {
      console.error("Error parsing phone array:", error);
      return [];
    }
  };

  // Process data from GraphQL query
  useEffect(() => {
    if (data) {
      if (data.multipleRemoved && data.multipleRemoved.length > 0) {
        let tempData = data.multipleRemoved.map((element) => ({
          ...element,
          courier: element.courier.toString().substring(2),
          occurrenceCount: parseInt(element.occurrenceCount.toString()),
          // Parse the Decimal array strings into actual numbers
          elapsedHrsAvgAllParsed: parseDecimalArray(element.elapsedHrsAvgAll),
          recipientsAllParsed: parsePhoneArray(element.recipientsAll),
          recipientsParsed: parsePhoneArray(element.recipients),
        }));
        setCsvData(tempData);
        setLoading(false);
        setNoResults(false);
      } else {
        setLoading(false);
        setNoResults(true);
      }
    }
  }, [data]);

  const handleSearch = () => {
    setloadInce(true);
    setLoading(true);
    QUERYHighRemovalCourier({
      variables: {
        operatorId: operatorId,
      },
    });
  };

  const recipientsSummaryTemplate = (rowData) => {
    const recipients = rowData.recipientsAllParsed || [];
    const uniqueCount = new Set(recipients).size;
    const totalCount = recipients.length;

    return (
      <div className="text-center">
        <p className="font-semibold text-sm">{uniqueCount} unique recipients</p>
        <p className="text-xs text-gray-600">{totalCount} removed packages</p>
      </div>
    );
  };

  const hoursDistributionTemplate = (rowData) => {
    const hours = rowData.elapsedHrsAvgAllParsed || [];
    if (!Array.isArray(hours) || hours.length === 0) {
      return <span className="text-red-500 text-xs">No data</span>;
    }

    let range0_1 = 0,
      range1_2 = 0,
      range2_above = 0;

    hours.forEach((hour) => {
      if (hour < 1) range0_1++;
      else if (hour <= 2) range1_2++;
      else range2_above++;
    });

    return (
      <div className="text-xs space-y-1">
        <div className="flex justify-between">
          <span>0-1h:</span>
          <span className="font-semibold">{range0_1}</span>
        </div>
        <div className="flex justify-between">
          <span>1-2h:</span>
          <span className="font-semibold">{range1_2}</span>
        </div>
        <div className="flex justify-between">
          <span>2h+:</span>
          <span className="font-semibold">{range2_above}</span>
        </div>
      </div>
    );
  };

  // ===== Progress Bar for Hours =====
  const hoursProgressTemplate = (rowData) => {
    const hours = rowData.elapsedHrsAvgAllParsed || [];
    const avgHours =
      hours.length > 0
        ? Math.round((hours.reduce((a, b) => a + b, 0) / hours.length) * 10) /
          10
        : 0;
    const maxValue = 24;
    const percentage = (avgHours / maxValue) * 100;

    return (
      <div className="flex flex-col gap-2">
        <div className="w-full bg-gray-300 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${
              percentage > 50 ? "bg-red-500" : "bg-yellow-500"
            }`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          ></div>
        </div>
        <span className="text-xs font-semibold">Avg: {avgHours}h</span>
      </div>
    );
  };

  // ===== Risk Badge =====
  const riskBadgeTemplate = (rowData) => {
    const count = rowData.occurrenceCount;
    let badgeColor = "bg-yellow-500";
    let badgeText = "Medium";

    if (count >= 10) {
      badgeColor = "bg-red-600";
      badgeText = "High";
    } else if (count >= 5) {
      badgeColor = "bg-orange-500";
      badgeText = "Medium";
    } else {
      badgeColor = "bg-green-500";
      badgeText = "Low";
    }

    return (
      <span
        className={`${badgeColor} text-white px-3 py-1 rounded-full text-xs font-semibold`}
      >
        {badgeText} ({count})
      </span>
    );
  };

  // ===== View Details Button =====
  const detailsButtonTemplate = (rowData) => {
    return (
      <div className="flex items-center justify-center">
        <Button
          label="View"
          icon="pi pi-eye"
          onClick={() => {
            setSelectedRow(rowData);
            setShowDetailsModal(true);
          }}
          className="p-2 justify-center text-center gap-0"
        />
      </div>
    );
  };

  const summarizeRecipients = (recipientsString) => {
    const recipientsArray = JSON.parse(recipientsString.replace(/'/g, '"'));

    const countOccurrences = recipientsArray.reduce((acc, number) => {
      acc[number] = (acc[number] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(countOccurrences).map(([number, count]) => (
      <React.Fragment key={number}>
        {number} - <b>{count}</b>
        <br />
      </React.Fragment>
    ));
  };

  const summarizeRecipients2 = (recipientsString, count) => {
    let recipientsArray = [];

    try {
      recipientsArray = JSON.parse(recipientsString);
    } catch (error) {
      console.error("Error parsing recipientsAll:", error);
      return "Invalid data";
    }

    if (!Array.isArray(recipientsArray)) {
      return "Data is not an array";
    }

    // Initialize counters for each range
    let range1_1 = 0; // 0 - 1
    let range1_2 = 0; // 1 - 2
    let range2Above = 0; // 2 and above

    // Tally the occurrences based on ranges
    recipientsArray.forEach((number) => {
      if (number >= 0 && number < 1) {
        range1_1++;
      } else if (number >= 1 && number <= 2) {
        range1_2++;
      } else if (number > 2) {
        range2Above++;
      }
    });

    // Return the summary with range counts
    return (
      <React.Fragment>
        <p className="text">
          0 - 1 hours: <b>{range1_1}</b>
          <br />1 - 2 hours: <b>{range1_2}</b>
          <br />2 hours above: <b>{range2Above}</b>
          <br />
        </p>
      </React.Fragment>
    );
  };

  // Custom body for the 'recipientsAll' column
  const recipientsBodyTemplate = (rowData) => {
    return summarizeRecipients(rowData.recipientsAll);
  };

  // Custom body for the 'recipientsAll' column
  const avgBodyTemplate = (rowData) => {
    return summarizeRecipients2(
      rowData.elapsedHrsAvgAll,
      rowData.occurrenceCount,
    );
  };

  return (
    <ContentContainer containerTitle={"High Removal Couriers"}>
      <Button
        value={"SEARCH"}
        onClick={handleSearch}
        className={"w-40 h-8 m-5"}
      />
      {loading && <LoadingComponent />}
      <div>
        <div>
          {csvData.length === 0 && !loading && loadInce && (
            <div>
              <NoDataFoundTemplate width={56} height={56} />
            </div>
          )}
        </div>
        {!loading && loadInce && !noResults && (
          <>
            <div className="flex flex-row gap-1">
              <p>Total: </p>
              <p className="font-bold">{csvData.length}</p>
            </div>
            <DownloadButton
              dt={dt}
              id={"High Removal Couriers"}
              csvOnly={true}
            />
            <div
              className="bg-primary-primaryYellow p-2 border-b-2 border-stone-400"
              id="High Removal Couriers"
            >
              <p className="font-bold text-center">
                High Removal Couriers For The Last 7 days
              </p>
              <DataTable
                paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Rows per page"
                paginator
                paginatorRight
                rowsPerPageOptions={[10, 20, 50]}
                rows={10}
                value={csvData}
                sortField="occurrenceCount"
                sortOrder={-1}
                tableStyle={{ minWidth: "50%" }}
                exportFilename="High Removal Couriers"
                stripedRows
                showGridlines
                className="border-2 border-stone-400"
                ref={dt}
              >
                <Column
                  header={"Agent"}
                  field="courier"
                  sortable
                  style={{ minWidth: "10%" }}
                  headerStyle={{
                    backgroundColor: "#facc15",
                    color: "black",
                    textAlign: "center",
                  }}
                />
                <Column
                  header={"Recipients"}
                  field="recipientsAll"
                  body={recipientsSummaryTemplate}
                  sortable
                  style={{ minWidth: "10%" }}
                  headerStyle={{
                    backgroundColor: "#facc15",
                    color: "black",
                    textAlign: "center",
                  }}
                />
                <Column
                  header={"Hours Breakdown"}
                  field="elapsedHrsAvgAll"
                  body={hoursDistributionTemplate}
                  style={{ minWidth: "12%" }}
                  headerStyle={{
                    backgroundColor: "#facc15",
                    color: "black",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                />

                <Column
                  header={"Hours Stayed on Locker"}
                  field="elapsedHrsAvgAll"
                  body={hoursProgressTemplate}
                  sortable
                  style={{ minWidth: "10%" }}
                  headerStyle={{
                    backgroundColor: "#facc15",
                    color: "black",
                    textAlign: "center",
                  }}
                />
                <Column
                  header={"Latest Remove Date"}
                  field="statusDate"
                  sortable
                  style={{ minWidth: "10%" }}
                  headerStyle={{
                    backgroundColor: "#facc15",
                    color: "black",
                    textAlign: "center",
                  }}
                />
                <Column
                  header={"Risk Level"}
                  field="occurrenceCount"
                  body={riskBadgeTemplate}
                  sortable
                  style={{ minWidth: "12%" }}
                  headerStyle={{
                    backgroundColor: "#facc15",
                    color: "black",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                />

                <Column
                  header={"Details"}
                  body={detailsButtonTemplate}
                  style={{ minWidth: "8%" }}
                  headerStyle={{
                    backgroundColor: "#facc15",
                    color: "black",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                />
                <Column
                  header={"Total Packages Removed"}
                  field="occurrenceCount"
                  sortable
                  style={{ minWidth: "10%", textAlign: "center" }}
                  headerStyle={{
                    backgroundColor: "#facc15",
                    color: "black",
                    textAlign: "center",
                  }}
                />
              </DataTable>
            </div>
          </>
        )}
      </div>

      <Dialog
        visible={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        header={`Courier Details: ${selectedRow?.courier}`}
        draggable={false}
        resizable={false}
        style={{ width: "90vw", maxWidth: "800px" }}
      >
        {selectedRow && (
          <div className="space-y-6">
            {/* Recipients Detail */}
            <Card title="Recipients" className="text-sm">
              <div className="grid grid-cols-3 gap-2">
                {(() => {
                  const recipients = selectedRow.recipientsAllParsed || [];
                  const counts = {};
                  recipients.forEach((r) => {
                    counts[r] = (counts[r] || 0) + 1;
                  });
                  return Object.entries(counts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([phone, count]) => (
                      <div
                        key={phone}
                        className="bg-yellow-50 p-2 rounded border border-yellow-200"
                      >
                        <p className="font-semibold text-xs">{phone}</p>
                        <p className="text-2xl font-bold text-red-600">
                          {count}
                        </p>
                      </div>
                    ));
                })()}
              </div>
            </Card>

            {/* Hours Breakdown Detail */}
            <Card title="Hours Stayed Breakdown" className="text-sm">
              <div className="space-y-2">
                {(() => {
                  const hours = selectedRow.elapsedHrsAvgAllParsed || [];
                  let range0_1 = 0,
                    range1_2 = 0,
                    range2_above = 0;

                  hours.forEach((hour) => {
                    if (hour < 1) range0_1++;
                    else if (hour <= 2) range1_2++;
                    else range2_above++;
                  });

                  return (
                    <>
                      <div className="flex justify-between p-2 bg-blue-50 rounded">
                        <span>0-1 hours:</span>
                        <span className="font-bold text-lg">{range0_1}</span>
                      </div>
                      <div className="flex justify-between p-2 bg-yellow-50 rounded">
                        <span>1-2 hours:</span>
                        <span className="font-bold text-lg">{range1_2}</span>
                      </div>
                      <div className="flex justify-between p-2 bg-red-50 rounded">
                        <span>2+ hours:</span>
                        <span className="font-bold text-lg">
                          {range2_above}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </Card>

            {/* Summary */}
            <Card className="bg-blue-50 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600">Total Packages Removed</p>
                  <p className="text-3xl font-bold text-red-600">
                    {selectedRow.occurrenceCount}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Latest Remove Date</p>
                  <p className="text-lg font-semibold">
                    {selectedRow.statusDate}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}
      </Dialog>
    </ContentContainer>
  );
}
